import { useEmailAuth } from '@/hooks/use-email-auth';
import { useProviderAuth } from '@/hooks/use-provider-auth';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Alert,
  Anchor,
  Box,
  BoxProps,
  Checkbox,
  Divider,
  Group,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
  PasswordInput,
} from '@mantine/core';
import { joiResolver, useForm } from '@mantine/form';
import { UserCredential } from 'firebase/auth';
import Joi from 'joi';
import React, { useEffect, useState, useRef } from 'react';
import GoogleSVG from '../../../public/img/icons/social/google.svg';
import Button from '../Button';
import FAIcon from '../FAIcon';

const Root = styled(Box, {
  shouldForwardProp: (prop: PropertyKey) => ![''].includes(prop.toString()),
})<Partial<LoginFormProps>>((props) => css``);

interface Props {
  onLogin: (creds: UserCredential) => void;
  onPasswordResetRequest?: () => void;
  onSignupRequest?: () => void;
  trustDevice?: boolean;
  onTrustDeviceChange?: (value: boolean) => void;
  children?: React.ReactNode;
}

export type LoginFormProps = Props & BoxProps;

const LoginForm: React.FC<LoginFormProps> = ({
  onLogin,
  onPasswordResetRequest,
  onSignupRequest,
  trustDevice = false,
  onTrustDeviceChange,
  ...props
}) => {
  const [googleAuth, googleAuthResponse] = useProviderAuth('google');
  const [login, loginResponse] = useEmailAuth();
  const [isHandlingAuth, setIsHandlingAuth] = useState<boolean>(false);
  const [showError, setShowError] = useState(true);
  const [showPassword, setShowPassword] = useState(true);
  const formRef = useRef<HTMLFormElement>(null);
  const [showVerificationScreen, setShowVerificationScreen] = useState(false);
  const [unverifiedUser, setUnverifiedUser] = useState<any>(null);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const [remainingCooldown, setRemainingCooldown] = useState<number>(0);
  const [resendCount, setResendCount] = useState<number>(0);

  const schema = Joi.object({
    email: Joi.string()
      .email({ tlds: { allow: false } })
      .required()
      .messages({
        'string.empty': 'Please enter an e-mail address',
        'string.email': 'Please enter a valid e-mail address',
      }),
    password: Joi.string().required().messages({
      'string.empty': 'Please enter a password',
    }),
  });

  const loginForm = useForm({
    initialValues: {
      email: '',
      password: '',
    },
    validate: joiResolver(schema),
  });

  // Handle autofill detection
  useEffect(() => {
    const checkAutofill = () => {
      const form = formRef.current;
      if (form) {
        const emailInput = form.querySelector('input[type="email"]') as HTMLInputElement;
        const passwordInput = form.querySelector('input[type="password"]') as HTMLInputElement;
        
        if (emailInput?.value) {
          loginForm.setFieldValue('email', emailInput.value);
        }
        if (passwordInput?.value) {
          loginForm.setFieldValue('password', passwordInput.value);
        }
      }
    };

    // Check immediately and after a short delay to catch browser autofill
    checkAutofill();
    setTimeout(checkAutofill, 100);
    setTimeout(checkAutofill, 500);
    setTimeout(checkAutofill, 1000);

    // Add mutation observer to detect changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
          checkAutofill();
        }
      });
    });

    const form = formRef.current;
    if (form) {
      observer.observe(form, {
        attributes: true,
        childList: true,
        characterData: true,
        subtree: true
      });
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const pending =
    googleAuthResponse.status === 'pending' ||
    loginResponse.status === 'pending' ||
    isHandlingAuth;

  useEffect(() => {
    if (!isHandlingAuth && loginResponse.status === 'complete' && loginResponse.info) {
      setIsHandlingAuth(true);
      const userCred = loginResponse.info as UserCredential;
      
      if (trustDevice && userCred.user) {
        localStorage.setItem(`trust_preference_${userCred.user.uid}`, 'true');
      }
      
      onLogin(userCred);
    }
  }, [loginResponse, onLogin, isHandlingAuth, trustDevice]);

  const handleForgotPassword = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onPasswordResetRequest) {
      onPasswordResetRequest();
    }
  };

  // Detect unverified email error and show verification screen
  useEffect(() => {
    if (loginResponse.status === 'error' && loginResponse.info) {
      const info = loginResponse.info as any;
      if (info.error?.cause === 'auth/email-not-verified' && info.user) {
        setUnverifiedUser(info.user);
        setShowVerificationScreen(true);
      }
    }
  }, [loginResponse]);

  // Cooldown countdown effect
  useEffect(() => {
    if (remainingCooldown > 0) {
      const timer = setInterval(() => {
        setRemainingCooldown((prev) => {
          if (prev <= 1) {
            setResendError(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [remainingCooldown]);

  // Sync rate limit status across all devices - checks every 3 seconds
  useEffect(() => {
    if (!showVerificationScreen || !unverifiedUser) return;

    const syncRateLimitStatus = async () => {
      try {
        // First check if email is already verified
        await unverifiedUser.reload();
        if (unverifiedUser.emailVerified) {
          console.log('✅ Email verified detected during sync on all devices (login)!');
          setShowVerificationScreen(false);
          onLogin({ user: unverifiedUser } as UserCredential);
          return; // Stop syncing if verified
        }

        const token = await unverifiedUser.getIdToken();

        // Use CHECK-ONLY endpoint (does NOT send email, only reads status)
        const statusResponse = await fetch('/api/auth/check-rate-limit', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        });

        const statusData = await statusResponse.json();

        // If verified according to server
        if (statusData.verified) {
          console.log('✅ Email already verified (confirmed by server - login)');
          await unverifiedUser.reload();
          if (unverifiedUser.emailVerified) {
            setShowVerificationScreen(false);
            onLogin({ user: unverifiedUser } as UserCredential);
          }
          return; // Stop syncing if verified
        }

        // Update UI based on rate limit status
        if (!statusData.canSend && statusData.remainingTime > 0) {
          // Still rate limited - update countdown with server time
          setRemainingCooldown(statusData.remainingTime);
          if (!resendError) {
            // User-friendly message for login form
            setResendError('A verification email was recently sent. Please check your inbox or wait to request another.');
          }
        } else if (statusData.canSend) {
          // Rate limit cleared - enable button on all devices
          setRemainingCooldown(0);
          setResendError(null);
        }
      } catch (error) {
        console.error('Error syncing rate limit status:', error);
      }
    };

    // Check immediately when screen loads
    syncRateLimitStatus();

    // Then sync every 3 seconds to keep all devices in sync
    const syncInterval = setInterval(syncRateLimitStatus, 3000);

    return () => clearInterval(syncInterval);
  }, [showVerificationScreen, unverifiedUser, resendError, onLogin]);

  // Email verification polling effect
  useEffect(() => {
    if (showVerificationScreen && unverifiedUser) {
      const checkEmailVerification = async () => {
        try {
          await unverifiedUser.reload();

          if (unverifiedUser.emailVerified) {
            console.log('✅ Email verified successfully during login!');
            setShowVerificationScreen(false);
            // Trigger login with verified user
            onLogin({ user: unverifiedUser } as UserCredential);
          }
        } catch (error) {
          console.error('Error checking email verification:', error);
        }
      };

      const pollInterval = setInterval(checkEmailVerification, 3000);
      checkEmailVerification();

      return () => clearInterval(pollInterval);
    }
  }, [showVerificationScreen, unverifiedUser, onLogin]);

  // Resend verification email handler with server-side rate limiting
  const handleResendVerification = async () => {
    if (!unverifiedUser) return;

    try {
      setResendingEmail(true);
      setResendSuccess(false);
      setResendError(null);

      // Refresh token to ensure it's not expired (force refresh)
      const token = await unverifiedUser.getIdToken(true);

      const rateLimitResponse = await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const rateLimitData = await rateLimitResponse.json();

      if (!rateLimitResponse.ok) {
        if (rateLimitData.rateLimited) {
          setRemainingCooldown(rateLimitData.remainingTime || 60);

          // Show friendlier message on first attempt in LoginForm
          if (resendCount === 0) {
            setResendError('A verification email was recently sent. Please check your inbox or wait before requesting another.');
          } else {
            setResendError(rateLimitData.error);
          }
          return;
        } else if (rateLimitData.alreadyVerified) {
          setResendError('Your email is already verified!');
          await unverifiedUser.reload();
          if (unverifiedUser.emailVerified) {
            setShowVerificationScreen(false);
            onLogin({ user: unverifiedUser } as UserCredential);
          }
          return;
        } else {
          throw new Error(rateLimitData.error || 'Failed to send email');
        }
      }

      // Server approved - send email
      const { sendEmailVerification } = await import('firebase/auth');
      await sendEmailVerification(unverifiedUser, {
        url: process.env.NEXT_PUBLIC_APP_URL || window.location.origin,
        handleCodeInApp: true,
      });

      // Increment resend count for UI display
      setResendCount(prev => prev + 1);
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);

    } catch (error: any) {
      console.error('Error resending verification email:', error);

      if (error?.code === 'auth/too-many-requests') {
        setResendError('Too many requests. Please wait a few minutes before trying again.');
        setRemainingCooldown(120);
      } else if (error?.code === 'auth/user-not-found') {
        setResendError('User account not found. Please try signing up again.');
      } else if (error?.code === 'auth/network-request-failed') {
        setResendError('Network error. Please check your connection and try again.');
      } else {
        setResendError(error instanceof Error ? error.message : 'Failed to send verification email');
      }
    } finally {
      setResendingEmail(false);
    }
  };

  // Show verification screen if email is not verified
  if (showVerificationScreen) {
    return (
      <Root {...props} p={30}>
        <Title size={32}>Verify Your Email</Title>
        <Text opacity={0.7} fz={14} mt={5}>
          Please verify your email address to continue
        </Text>
        <Divider my={20} />

        <Alert
          icon={<FAIcon name="info-circle" size={16} />}
          title="Email Verification Required"
          color="blue"
          radius="md"
        >
          <Stack gap="sm">
            <Text size="sm">
              Your account email <strong>{unverifiedUser?.email}</strong> needs to be verified before you can login.
            </Text>
            <Text size="sm" c="dimmed">
              A verification email may have already been sent when you created your account. Please check your inbox (and spam folder).
            </Text>
            <Text size="sm" c="dimmed">
              If you didn't receive it, click the button below to send a new verification email.
            </Text>
            <Text size="sm" c="dimmed" fs="italic">
              This screen will automatically close once you verify your email.
            </Text>
            <Group gap="xs" mt="xs">
              <FAIcon name="spinner" size={14} />
              <Text size="xs" c="dimmed">
                Waiting for verification...
              </Text>
            </Group>
          </Stack>
        </Alert>

        {resendSuccess && (
          <Alert
            icon={<FAIcon name="check-circle" size={16} />}
            title="Email Sent!"
            color="green"
            radius="md"
            mt={15}
          >
            <Text size="sm">
              Verification email has been resent. Please check your inbox.
            </Text>
          </Alert>
        )}

        {resendError && (
          <Alert
            icon={<FAIcon name="exclamation-circle" size={16} />}
            title="Cannot Send Email"
            color="orange"
            radius="md"
            mt={15}
          >
            <Stack gap="xs">
              <Text size="sm">{resendError}</Text>
              {remainingCooldown > 0 && (
                <Group gap="xs">
                  <FAIcon name="clock" size={14} />
                  <Text size="sm" fw={600}>
                    Please wait {remainingCooldown} second{remainingCooldown !== 1 ? 's' : ''}
                  </Text>
                </Group>
              )}
            </Stack>
          </Alert>
        )}

        <Box mt={20}>
          <Divider my={15} />

          <Stack gap="xs">
            <Button
              component="button"
              variant="subtle"
              color="blue"
              size="small"
              fullwidth
              onClick={handleResendVerification}
              {...(resendingEmail && { loading: true })}
              disabled={resendingEmail || resendSuccess || remainingCooldown > 0}
              leftSection={<FAIcon name="envelope" size={14} />}
              transition={false}
            >
              {resendSuccess
                ? 'Email Sent!'
                : remainingCooldown > 0
                ? `Wait ${remainingCooldown}s`
                : resendCount > 0
                ? 'Resend Verification Email'
                : 'Send Verification Email'}
            </Button>
          </Stack>

          <Divider my={15} />

          <Button
            component="button"
            variant="outline"
            color="gray"
            size="small"
            fullwidth
            onClick={() => {
              setShowVerificationScreen(false);
              setUnverifiedUser(null);
              setResendCount(0);
              setResendError(null);
              setRemainingCooldown(0);
            }}
            leftSection={<FAIcon name="arrow-left" size={14} />}
            transition={false}
          >
            Back to Login
          </Button>
        </Box>
      </Root>
    );
  }

  return (
    <Root {...props} p={30}>
      <Title size={32}>Login</Title>

      <Text opacity={0.7} fz={14} mt={5}>
        Please enter your details to continue
      </Text>

      <Divider my={20} />

      <form
        ref={formRef}
        onSubmit={loginForm.onSubmit(async ({ email, password }) =>
          login(email, password)
        )}
        autoComplete="on"
        name="login"
      >
        <Stack gap="md">
          <TextInput
            label={<strong>Email</strong>}
            placeholder="E-mail address"
            type="email"
            name="email"
            id="email"
            autoComplete="email"
            disabled={pending}
            leftSection={<FAIcon name="envelope" size={14} />}
            {...loginForm.getInputProps('email')}
          />

          <PasswordInput
            label={<strong>Password</strong>}
            placeholder="Enter your password"
            name="password"
            id="password"
            autoComplete="current-password"
            disabled={pending}
            defaultVisible={true}
            visible={showPassword}
            onVisibilityChange={setShowPassword}
            leftSection={<FAIcon name="lock" size={14} />}
            {...loginForm.getInputProps('password')}
          />

          <Group justify="space-between" align="center">
            <Checkbox
              label={
                <Box>
                  <Text size="sm" component="span">
                    Remember this device for 30 days
                  </Text>
                  <Text size="xs" c="dimmed" mt={2} component="div">
                    Skip verification codes for future logins
                  </Text>
                </Box>
              }
              checked={trustDevice}
              onChange={(event) => onTrustDeviceChange?.(event.currentTarget.checked)}
            />
            <Anchor
              size="sm"
              c="dark"
              fw={500}
              onClick={handleForgotPassword}
              style={{ cursor: 'pointer' }}
            >
              Forgot password?
            </Anchor>
          </Group>

          {loginResponse.status === 'error' && loginResponse.info && (loginResponse.info as any).error?.cause !== 'auth/email-not-verified' && (
            <Alert
              color="red"
              title="Login Error"
              icon={<FAIcon name="exclamation-circle" size={16} />}
              styles={{
                root: {
                  backgroundColor: '#FFF5F5',
                  border: '1px solid #FED7D7'
                },
                title: {
                  color: '#C53030'
                },
                message: {
                  color: '#E53E3E'
                }
              }}
            >
              {(
                {
                  'auth/invalid-credential': 'Incorrect email or password. Please try again or reset your password.',
                  'auth/wrong-password': 'Incorrect email or password. Please try again or reset your password.',
                  'auth/user-not-found': 'No account found with this email. Please check your email or sign up.',
                  'auth/too-many-requests': 'Too many failed attempts. Please try again later or reset your password.',
                } as any
              )[(loginResponse.info as any).error?.cause || loginResponse.info.cause] ?? loginResponse.info.message}
            </Alert>
          )}

          <Button
            component="button"
            fullwidth
            transition={false}
            color="dark"
            type="submit"
            {...(pending && { loading: true })}
            disabled={pending}
            leftSection={<FAIcon name="sign-in-alt" size={14} />}
          >
            Sign in
          </Button>
        </Stack>
      </form>

      <Divider mt={30} mb={15} label={<Text>or</Text>} />

      <SimpleGrid cols={1} spacing={10}>
        <Button
          component="button"
          color="default"
          fullwidth
          radius="sm"
          transition={false}
          disabled={pending}
          onClick={googleAuth}
          leftSection={
            <Box w={16} style={{ display: 'flex', alignContent: 'center' }}>
              <GoogleSVG />
            </Box>
          }
        >
          <Text fz={14} fw={500}>
            Sign in with Google
          </Text>
        </Button>
      </SimpleGrid>

      <Text size="sm" mt={30} ta="center">
        Don&lsquo;t have an account? &nbsp;
        <Anchor
          c="dark"
          fw={500}
          onClick={onSignupRequest}
          style={{ cursor: 'pointer' }}
        >
          Sign up
        </Anchor>
      </Text>

      <Group mt={30} justify="center">
        <Anchor
          href="https://www.homehealthsolutions.io/terms"
          target="_blank"
          rel="noopener noreferrer"
          c="gray.6"
          size="11px"
        >
          Terms and Conditions
        </Anchor>

        <Anchor
          href="https://www.homehealthsolutions.io/privacy"
          target="_blank"
          rel="noopener noreferrer"
          c="gray.6"
          size="11px"
        >
          Privacy Policy
        </Anchor>
      </Group>
    </Root>
  );
};

export default LoginForm;